import { apiFetch, apiJson } from './client';
import type {
  CatalogColumn,
  CatalogDatabase,
  CatalogSchema,
  CatalogTable,
  Duckling,
  NotebookDto,
  QueryHistoryPage,
  RegisteredFile,
  SubmitQueryRequest,
  SubmitQueryResponse,
} from '@/types/api';

export function submitQuery(request: SubmitQueryRequest): Promise<SubmitQueryResponse> {
  return apiJson<SubmitQueryResponse>('/queries', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function cancelQuery(queryId: string): Promise<void> {
  await apiFetch(`/queries/${encodeURIComponent(queryId)}/cancel`, { method: 'POST' });
}

export function fetchHistory(search?: string): Promise<QueryHistoryPage> {
  const query = search ? `?q=${encodeURIComponent(search)}` : '';
  return apiJson<QueryHistoryPage>(`/queries/history${query}`);
}

export function fetchDatabases(): Promise<readonly CatalogDatabase[]> {
  return apiJson<readonly CatalogDatabase[]>('/catalog/databases');
}

export function fetchSchemas(database?: string): Promise<readonly CatalogSchema[]> {
  const query = database ? `?database=${encodeURIComponent(database)}` : '';
  return apiJson<readonly CatalogSchema[]>(`/catalog/schemas${query}`);
}

export function fetchTables(database?: string, schema?: string): Promise<readonly CatalogTable[]> {
  const params = new URLSearchParams();
  if (database) params.set('database', database);
  if (schema) params.set('schema', schema);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  return apiJson<readonly CatalogTable[]>(`/catalog/tables${query}`);
}

export function fetchColumns(
  database?: string,
  schema?: string,
  table?: string,
): Promise<readonly CatalogColumn[]> {
  const params = new URLSearchParams();
  if (database) params.set('database', database);
  if (schema) params.set('schema', schema);
  if (table) params.set('table', table);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  return apiJson<readonly CatalogColumn[]>(`/catalog/columns${query}`);
}

/**
 * The fleet, unwrapped from its envelope.
 *
 * `GET /ducklings` answers `{mode, count, ducklings}` -- FleetController returns
 * a Map, not a bare list -- and this used to be typed as the array itself. The
 * panel then called `.map` on an object and rendered nothing but
 * "(e.data ?? []).map is not a function". No test caught it because the MSW
 * handler replied with a bare array, so the mock disagreed with the service it
 * was standing in for. A bare array is still accepted, since that is the shape
 * a reasonable reader would expect this endpoint to have.
 */
export async function fetchDucklings(): Promise<readonly Duckling[]> {
  const body = await apiJson<unknown>('/ducklings');
  if (Array.isArray(body)) return body as readonly Duckling[];

  const ducklings = (body as { ducklings?: unknown } | null)?.ducklings;
  return Array.isArray(ducklings) ? (ducklings as readonly Duckling[]) : [];
}

export function uploadParquet(file: File, targetSchema = 'uploads'): Promise<RegisteredFile> {
  const form = new FormData();
  form.append('file', file);
  form.append('schema', targetSchema);
  return apiJson<RegisteredFile>('/files', { method: 'POST', body: form });
}

/* Notebooks are persisted to the backend as well as localStorage, so a user's
   work follows them to another browser. localStorage remains the source of
   truth for unsaved edits. */

export function fetchNotebooks(): Promise<readonly NotebookDto[]> {
  return apiJson<readonly NotebookDto[]>('/notebooks');
}

export function saveNotebook(notebook: NotebookDto): Promise<NotebookDto> {
  return apiJson<NotebookDto>(`/notebooks/${encodeURIComponent(notebook.id)}`, {
    method: 'PUT',
    body: JSON.stringify(notebook),
  });
}
